import { NgClass, NgForOf, NgIf } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Directory } from '../../../../../core/interfaces/directory.interface';
import { MenuItem } from 'primeng/api';
import { ContextMenuModule } from 'primeng/contextmenu';
import { Tooltip } from 'primeng/tooltip';
import { IFile } from '../../../../../core/interfaces/file.interface';

enum ViewMode {
    LIST,
    GRID
}

@Component({
    selector: 'app-directory-list',
    standalone: true,
    templateUrl: './directory-list.component.html',
    styleUrl: './directory-list.component.scss',
    imports: [NgIf, NgForOf, ContextMenuModule, NgClass, Tooltip]
})
export class DirectoryListComponent implements OnInit {
    @Input() directories: Directory[] = [];
    @Input() currentDirectory: Directory | null = null;
    @Input() viewMode: ViewMode = ViewMode.GRID;
    @Output() directorySelected = new EventEmitter<Directory>();
    @Output() renameDirectorySelected = new EventEmitter<Directory>();
    @Output() createSubdirectory = new EventEmitter<Directory>();
    @Output() moveToDirectory = new EventEmitter<Directory>();
    @Output() uploadFileToDirectory = new EventEmitter<Directory>();
    @Output() deleteDirectory = new EventEmitter<Directory>();
    @Output() directoryProperties = new EventEmitter<Directory>();
    @Output() droppedFilesIntoDirectory = new EventEmitter<File[]>();

    ViewMode = ViewMode;

    directoryMenuItems: MenuItem[] = [];
    selectedDirectoryForMenu: Directory | null = null;

    private dragCounter = 0;
    isDragging = false;

    ngOnInit(): void {
        this.directoryMenuItems = [
            {
                label: 'Criar pasta',
                icon: 'pi pi-folder-plus',
                command: () => this.selectedDirectoryForMenu && this.createSubdirectory.emit(this.selectedDirectoryForMenu)
            },
            {
                label: 'Renomear',
                icon: 'pi pi-pencil',
                visible: !!this.currentDirectory?.alias,
                command: () => this.selectedDirectoryForMenu && this.renameDirectorySelected.emit(this.selectedDirectoryForMenu)
            },
            {
                label: 'Mover para outro diretório',
                icon: 'pi pi-arrow-right-arrow-left',
                command: () => this.selectedDirectoryForMenu && this.moveToDirectory.emit(this.selectedDirectoryForMenu)
            },
            {
                label: 'Enviar arquivo',
                icon: 'pi pi-upload',
                command: () => this.selectedDirectoryForMenu && this.uploadFileToDirectory.emit(this.selectedDirectoryForMenu)
            },
            {
                label: 'Mover para lixeira',
                icon: 'pi pi-trash',
                disabled: false,
                command: () => this.selectedDirectoryForMenu && this.deleteDirectory.emit(this.selectedDirectoryForMenu)
            },
            {
                label: 'Propriedades',
                icon: 'pi pi-info-circle',
                command: () => this.selectedDirectoryForMenu && this.directoryProperties.emit(this.selectedDirectoryForMenu)
            }
        ];
    }

    selectDirectory(directory: Directory): void {
        this.currentDirectory = directory;
        this.directorySelected.emit(directory);
    }

    onDirectoryContextMenu(event: MouseEvent, directory: Directory, menu: any): void {
        event.preventDefault();
        this.selectedDirectoryForMenu = directory;
        this.directoryMenuItems = [...this.directoryMenuItems];
        menu.show(event);
    }

    onDragEnter(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.dragCounter++; // Incrementa ao entrar em qualquer sub-elemento

        if (event.dataTransfer?.types.includes('Files')) {
            this.isDragging = true;
        }
    }

    onDragOver(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.dragCounter--;

        if (this.dragCounter === 0) {
            this.isDragging = false;
        }

        if (!this.canDrop()) return;

        if (event.dataTransfer) {
            event.dataTransfer.dropEffect = 'copy';
        }

        this.isDragging = true;
    }

    onDragLeave(event: DragEvent) {
        event.preventDefault();
        if (!this.canDrop()) return;

        this.dragCounter--;

        if (this.dragCounter === 0) {
            this.isDragging = false;
        }
        this.isDragging = false;
    }

    // async onDrop(event: DragEvent) {
    //     event.preventDefault();
    //     event.stopPropagation();
    //     this.isDragging = false;
    //     this.dragCounter = 0;

    //     const items = event.dataTransfer?.items;
    //     if (!items || !this.currentDirectory) return;

    //     const files: File[] = [];

    //     // Função recursiva para ler entradas de diretório
    //     const traverseEntry = async (entry: any, path: string = '') => {
    //         if (entry.isFile) {
    //             const file = await new Promise<File>((resolve) => entry.file(resolve));
    //             // Injetamos o caminho relativo manualmente se não existir
    //             const relativePath = path + file.name;
    //             Object.defineProperty(file, 'webkitRelativePath', { value: relativePath });
    //             files.push(file);
    //         } else if (entry.isDirectory) {
    //             const dirReader = entry.createReader();
    //             const entries = await new Promise<any[]>((resolve) => dirReader.readEntries(resolve));
    //             for (const childEntry of entries) {
    //                 await traverseEntry(childEntry, path + entry.name + '/');
    //             }
    //         }
    //     };

    //     const promises = [];
    //     for (let i = 0; i < items.length; i++) {
    //         const entry = items[i].webkitGetAsEntry();
    //         if (entry) promises.push(traverseEntry(entry));
    //     }

    //     await Promise.all(promises);

    //     if (files.length) {
    //         // Emita para o pai (shared-directory)
    //         this.droppedFilesIntoDirectory.emit(files);
    //     }
    // }

    async onDrop(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.isDragging = false;

        const items = event.dataTransfer?.items;
        if (!items || !this.currentDirectory) return;

        const CHUNK_SIZE = 500; // Tamanho do lote
        let currentChunk: File[] = [];
        const queue: { entry: any; path: string }[] = [];

        for (let i = 0; i < items.length; i++) {
            const entry = items[i].webkitGetAsEntry();
            if (entry) queue.push({ entry, path: '' });
        }

        while (queue.length > 0) {
            const { entry, path } = queue.shift()!;

            if (entry.isFile) {
                const file = await this.getFileFromEntry(entry);
                const relativePath = path + file.name;
                Object.defineProperty(file, 'webkitRelativePath', {
                    value: relativePath,
                    configurable: true
                });

                currentChunk.push(file);

                // Quando atingir o tamanho do chunk, emite e limpa
                if (currentChunk.length >= CHUNK_SIZE) {
                    this.droppedFilesIntoDirectory.emit([...currentChunk]);
                    currentChunk = [];
                    // Pequeno fôlego para a Main Thread não travar a UI
                    await new Promise((resolve) => setTimeout(resolve, 0));
                }
            } else if (entry.isDirectory) {
                const dirEntries = await this.readAllEntries(entry);
                for (const child of dirEntries) {
                    queue.push({ entry: child, path: path + entry.name + '/' });
                }
            }
        }

        // Emite o restante (último pedaço)
        if (currentChunk.length > 0) {
            this.droppedFilesIntoDirectory.emit(currentChunk);
        }
    }

    // Auxiliar para ler TODAS as entradas de uma pasta (corrige o limite de 100 itens)
    private async readAllEntries(directoryEntry: any): Promise<any[]> {
        const reader = directoryEntry.createReader();
        const allEntries: any[] = [];

        const readBatch = async (): Promise<any[]> => {
            return new Promise((resolve, reject) => {
                reader.readEntries(resolve, reject);
            });
        };

        let batch = await readBatch();
        while (batch.length > 0) {
            allEntries.push(...batch);
            batch = await readBatch(); // O navegador retorna vazio quando termina
        }
        return allEntries;
    }

    // Auxiliar para converter entry em File
    private getFileFromEntry(fileEntry: any): Promise<File> {
        return new Promise((resolve, reject) => fileEntry.file(resolve, reject));
    }

    canDrop(): boolean {
        return !!this.currentDirectory && this.currentDirectory.id !== 'root';
    }

    delete(directory: any, event: Event): void {
        event.stopPropagation();
        this.deleteDirectory.emit(directory);
    }

    getParentName(parentId: string | null): string {
        if (!parentId) return 'Raiz';
        return parentId ? `em ${this.currentDirectory?.name}` : 'Diretório anterior';
    }

    printDir(directory: Directory) {
        console.log('Directory:', directory);
    }

    getSharedNames(directory: any): string {
        const group = directory?.sharedGroup ?? [];
        if (!group.length) return '';

        const firstNames = group.map((u: any) => u?.name?.split(' ')[0] ?? 'Usuário');

        if (firstNames.length === 1) return firstNames[0];

        if (firstNames.length === 2) {
            return `${firstNames[0]} e ${firstNames[1]}`;
        }

        return `${firstNames[0]}, ${firstNames[1]} +${firstNames.length - 2}`;
    }

    getFullSharedNames(directory: any): string {
        return "Permitido para " + (directory?.sharedGroup ?? []).map((u: any) => u?.name ?? 'Usuário').join(', ');
    }

    getInitials(name?: string): string {
        if (!name) return '?';

        const parts = name.trim().split(' ');

        if (parts.length === 1) {
            return parts[0].substring(0, 2).toUpperCase();
        }

        return (parts[0][0] + parts[1][0]).toUpperCase();
    }
}
